import React, { useState, useMemo } from "react";
import { ArrowLeft, Filter, Users, Mic, Video, Flame, Crown, Heart, Search, X, Star } from "lucide-react";
import { ExhibitionBooth, ExhibitionCountry, ExhibitionCity } from "@/hooks/useExhibition";
import { LiveBadge } from "@/components/live-arenas/LiveBadge";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import IntentIcon, { INTENT_LABELS, INTENT_COLORS, INTENT_ICONS } from "./IntentIcon";
import FavoriteButton from "./FavoriteButton";

interface ExhibitionBoothDirectoryProps {
  city: ExhibitionCity;
  country: ExhibitionCountry;
  booths: ExhibitionBooth[];
  loading: boolean;
  remainingRequests: number;
  favoriteBoothIds: string[];
  onBack: () => void;
  onPreviewBooth: (boothId: string) => void;
  onEnterBooth: (booth: ExhibitionBooth) => void;
  onToggleBoothFavorite: (boothId: string) => void;
  createBoothModal?: React.ReactNode;
}

const ExhibitionBoothDirectory: React.FC<ExhibitionBoothDirectoryProps> = ({
  city,
  country,
  booths,
  loading,
  remainingRequests,
  favoriteBoothIds,
  onBack,
  onPreviewBooth,
  onEnterBooth,
  onToggleBoothFavorite,
  createBoothModal,
}) => {
  const [selectedIntent, setSelectedIntent] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const filteredBooths = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return booths.filter((booth) => {
      // Favorites filter
      if (showFavoritesOnly && !favoriteBoothIds.includes(booth.id)) return false;
      if (selectedIntent && booth.exhibition_intent !== selectedIntent) return false;
      if (selectedLanguage && booth.language !== selectedLanguage) return false;
      if (query) {
        const matchesName = booth.name.toLowerCase().includes(query);
        const matchesHost = booth.host_name?.toLowerCase().includes(query);
        const matchesTopic = booth.topic?.toLowerCase().includes(query);
        const matchesDescription = booth.description?.toLowerCase().includes(query);
        if (!matchesName && !matchesHost && !matchesTopic && !matchesDescription) return false;
      }
      return true;
    });
  }, [booths, selectedIntent, selectedLanguage, searchQuery, showFavoritesOnly, favoriteBoothIds]);

  // Separate favorited booths for priority display
  const favoritedBooths = useMemo(() => 
    filteredBooths.filter(b => favoriteBoothIds.includes(b.id)),
    [filteredBooths, favoriteBoothIds]
  );

  const nonFavoritedBooths = useMemo(() =>
    showFavoritesOnly ? [] : filteredBooths.filter(b => !favoriteBoothIds.includes(b.id)),
    [filteredBooths, favoriteBoothIds, showFavoritesOnly]
  );

  const intents = [...new Set(booths.map(b => b.exhibition_intent).filter(Boolean))];
  const languages = [...new Set(booths.map(b => b.language))];

  return (
    <div className="min-h-screen sky-gradient relative overflow-hidden">
      <BackgroundBalloons />

      <div className="relative z-10 flex flex-col min-h-screen pb-20">
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <div className="flex items-center gap-2">
              {createBoothModal}
              <LiveBadge variant="glow" />
            </div>
          </div>

          <div className="text-center mt-2">
            <h1 className="text-2xl font-bold text-foreground">{city.name}</h1>
            <p className="text-muted-foreground text-sm flex items-center justify-center gap-1">
              <span>{country.flag}</span>
              <span>{country.name} • Love Exhibition</span>
            </p>
          </div>
        </div>

        {/* Search Input */}
        {booths.length > 3 && (
          <div className="px-4 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search booths, hosts, topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50 focus-visible:ring-1 focus-visible:ring-primary"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-4 mt-3">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Badge
              variant={selectedIntent === null ? "default" : "outline"}
              className="cursor-pointer whitespace-nowrap"
              onClick={() => setSelectedIntent(null)}
            >
              All
            </Badge>
            {intents.map((intent) => {
              const Icon = INTENT_ICONS[intent || ""] || Heart;
              return (
                <Badge
                  key={intent}
                  variant={selectedIntent === intent ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer whitespace-nowrap flex items-center gap-1",
                    selectedIntent === intent && `bg-gradient-to-r ${INTENT_COLORS[intent || ""]}`
                  )}
                  onClick={() => setSelectedIntent(intent === selectedIntent ? null : intent)}
                >
                  <Icon className="w-3 h-3" />
                  {INTENT_LABELS[intent || ""] || intent}
                </Badge>
              );
            })}
            {languages.length > 1 && (
              <>
                <div className="w-px h-5 bg-border" />
                {languages.map((lang) => (
                  <Badge
                    key={lang}
                    variant={selectedLanguage === lang ? "default" : "outline"}
                    className="cursor-pointer whitespace-nowrap uppercase text-[10px]"
                    onClick={() => setSelectedLanguage(lang === selectedLanguage ? null : lang)}
                  >
                    {lang}
                  </Badge>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Request counter & Favorites toggle */}
        <div className="mx-4 mt-3 flex gap-2">
          <div className="flex-1 p-2 rounded-xl bg-white/40 backdrop-blur-sm border border-white/50">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Join Requests Today</span>
              <span className="font-bold text-pop">{remainingRequests} / 5</span>
            </div>
          </div>
          {favoriteBoothIds.length > 0 && (
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={cn(
                "p-2 rounded-xl backdrop-blur-sm border transition-all flex items-center gap-1.5",
                showFavoritesOnly
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-white/40 border-white/50 text-muted-foreground hover:text-amber-500"
              )}
              title="Show favorites only"
            >
              <Star className={cn("w-4 h-4", showFavoritesOnly && "fill-white")} />
              <span className="text-xs font-medium">{favoriteBoothIds.length}</span>
            </button>
          )}
        </div>

        {/* Booths list */}
        <div className="flex-1 overflow-y-auto px-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 rounded-full border-4 border-pop border-t-transparent animate-spin" />
            </div>
          ) : filteredBooths.length === 0 ? (
            <div className="text-center py-12">
              {showFavoritesOnly ? (
                <>
                  <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No favorited booths match your filters</p>
                  <button
                    onClick={() => setShowFavoritesOnly(false)}
                    className="mt-2 text-sm text-primary hover:underline"
                  >
                    Show all booths
                  </button>
                </>
              ) : (
                <>
                  <Heart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No booths match your filters</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4 pb-8">
              {/* Favorited Booths Section */}
              {favoritedBooths.length > 0 && !showFavoritesOnly && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white">
                      <Star className="w-4 h-4 fill-white" />
                      <span className="text-sm font-bold">Favorites</span>
                    </div>
                    <span className="text-xs text-muted-foreground">({favoritedBooths.length})</span>
                  </div>
                  
                  {favoritedBooths.map((booth) => (
                    <BoothCard
                      key={`fav-${booth.id}`}
                      booth={booth}
                      isFavorite={true}
                      onPreview={() => onPreviewBooth(booth.id)}
                      onEnter={() => onEnterBooth(booth)}
                      onToggleFavorite={() => onToggleBoothFavorite(booth.id)}
                    />
                  ))}
                </div>
              )}

              {/* All/Non-favorited Booths */}
              {(showFavoritesOnly ? favoritedBooths : nonFavoritedBooths).length > 0 && (
                <div className="space-y-3">
                  {favoritedBooths.length > 0 && !showFavoritesOnly && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">All Booths</span>
                      <span className="text-xs text-muted-foreground">({nonFavoritedBooths.length})</span>
                    </div>
                  )}
                  
                  {(showFavoritesOnly ? favoritedBooths : nonFavoritedBooths).map((booth) => (
                    <BoothCard
                      key={booth.id}
                      booth={booth}
                      isFavorite={favoriteBoothIds.includes(booth.id)}
                      onPreview={() => onPreviewBooth(booth.id)}
                      onEnter={() => onEnterBooth(booth)}
                      onToggleFavorite={() => onToggleBoothFavorite(booth.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface BoothCardProps {
  booth: ExhibitionBooth;
  isFavorite: boolean;
  onPreview: () => void;
  onEnter: () => void;
  onToggleFavorite: () => void;
}

const BoothCard: React.FC<BoothCardProps> = ({ booth, isFavorite, onPreview, onEnter, onToggleFavorite }) => {
  const isFull = booth.participants_count >= booth.max_participants;
  const isHot = booth.participants_count >= booth.max_participants * 0.7;
  const intent = booth.exhibition_intent || "casual";

  return (
    <div
      className={cn(
        "relative rounded-2xl overflow-hidden backdrop-blur-xl bg-white/50 border border-white/60 shadow-lg transition-all duration-300 hover:shadow-xl",
        isHot && "ring-2 ring-amber-400/50",
        isFavorite && "ring-2 ring-amber-500/50"
      )}
    >
      {/* Intent badge with icon */}
      <IntentIcon intent={intent} size="md" className="absolute top-3 left-3 z-10" />

      {/* Favorite button */}
      <FavoriteButton
        isFavorite={isFavorite}
        onClick={onToggleFavorite}
        size="sm"
        className="absolute top-12 left-3 z-10"
      />

      {/* Hot badge */}
      {isHot && (
        <div className="absolute top-3 right-3 z-10">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-medium animate-pulse">
            <Flame className="w-3 h-3" />
            HOT
          </span>
        </div>
      )}

      <div className="pt-12 p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          {booth.host_photo && (
            <div className="relative">
              <img
                src={booth.host_photo}
                alt={booth.host_name}
                className="w-12 h-12 rounded-full object-cover border-2 border-white"
              />
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                <Crown className="w-3 h-3 text-white" />
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-foreground truncate">{booth.name}</h3>
            <p className="text-sm text-muted-foreground truncate">
              Hosted by {booth.host_name || "Anonymous"}
            </p>
          </div>
        </div>

        {/* Topic */}
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {booth.description || booth.topic}
        </p>

        {/* Preview faces */}
        {(booth.featured_participants?.length ?? 0) > 0 && (
          <div className="flex -space-x-2 mb-3">
            {booth.featured_participants?.filter(p => p.photo_url).slice(0, 6).map((p, i) => (
              <img
                key={i}
                src={p.photo_url!}
                alt={p.name}
                className="w-8 h-8 rounded-full border-2 border-white object-cover"
              />
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className={cn("font-semibold", isHot && "text-orange-500")}>
                {booth.participants_count}
              </span>
              <span className="text-muted-foreground">/ {booth.max_participants}</span>
            </span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
              {booth.is_video_enabled ? <Video className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
              {booth.is_video_enabled ? "Video" : "Voice"}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase">
              {booth.language}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={onPreview}
          >
            Preview
          </Button>
          {booth.is_joined ? (
            <Button
              className="flex-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
              onClick={onEnter}
            >
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              Enter
            </Button>
          ) : isFull ? (
            <Button disabled className="flex-1 rounded-full bg-muted text-muted-foreground">
              Full
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ExhibitionBoothDirectory;
